import * as Select from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import "./select.css";

interface Option {
  value: string;
  label: string;
}

interface CustomSelectProps {
  options: Option[];
  placeholder?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  borderColor?: string;
  textColor?: string;
  backgroundColor?: string;
  hoverColor?: string;
  focusRingColor?: string;
  error?: boolean;
}

export default function CustomSelect({
  options,
  placeholder = "Selecione",
  onValueChange,
  className = "",
  borderColor = "border-gray-300",
  textColor = "text-white",
  backgroundColor = "bg-black",
  hoverColor = "hover:bg-blue-800",
  focusRingColor = "focus:ring-primary-600",
  error = false,
}: CustomSelectProps) {
  const [value, setValue] = useState("");
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile devices
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check on initial load
    checkIfMobile();

    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);

    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Handle initial value from form registration
  useEffect(() => {
    // If the component is controlled by a form (like react-hook-form)
    // this ensures the initial value is properly set
    const defaultValue = options.length > 0 ? options[0].value : "";
    if (value === "" && defaultValue) {
      setValue(defaultValue);
      if (onValueChange) {
        onValueChange(defaultValue);
      }
    }
  }, [options]);

  const handleValueChange = (newValue: string) => {
    setValue(newValue);
    if (onValueChange) {
      onValueChange(newValue);
    }
  };

  return (
    <div className={className}>
      <Select.Root value={value} onValueChange={handleValueChange}>
        <Select.Trigger
          className={`inline-flex items-center justify-between ${backgroundColor} w-full px-4 py-2.5 border ${error ? "border-red-500" : borderColor} rounded-md shadow-sm focus:ring ${focusRingColor} ${textColor} appearance-none relative
          ${isMobile ? 'mobile-select-trigger min-h-[42px]' : ''}
          transition-all duration-200 hover:border-primary-500`}
          style={{
            WebkitAppearance: 'none',
            MozAppearance: 'none'
          }}
          aria-label={placeholder}
          aria-haspopup="listbox"
          data-mobile={isMobile ? "true" : "false"}
        >
          <Select.Value placeholder={placeholder} className="flex-1 text-left truncate">
            {options.find((opt) => opt.value === value)?.label || placeholder}
          </Select.Value>
          <Select.Icon className="ml-2 flex-shrink-0">
            <ChevronDown className={`w-5 h-5 ${isMobile ? 'text-primary-500' : ''}`} />
          </Select.Icon>
        </Select.Trigger>

        <Select.Portal>
          <Select.Content
            className={`${backgroundColor} ${textColor} border ${borderColor} rounded-md shadow-lg z-50 max-h-[var(--radix-select-content-available-height)] overflow-hidden
            ${isMobile ? 'w-[var(--radix-select-trigger-width)] min-w-[260px]' : ''}`}
            position={isMobile ? "popper" : "item-aligned"}
            sideOffset={isMobile ? 5 : 0}
          >
            <Select.Viewport className={`p-2 ${isMobile ? 'max-h-[300px]' : ''}`}>
              {options.map((option) => (
                <Select.Item
                  key={option.value}
                  value={option.value}
                  className={`flex items-center justify-between px-4 py-2.5 cursor-pointer ${hoverColor} outline-none rounded
                  ${isMobile ? 'my-1 active:bg-primary-700' : ''}`}
                >
                  <Select.ItemText>{option.label}</Select.ItemText>
                  <Select.ItemIndicator>
                    <Check className="w-4 h-4" />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </div>
  );
}
